import { Injectable, inject } from '@angular/core';
import { Observable, from, map, catchError, throwError } from 'rxjs';
import {
    createWorkflowWebClient,
    ServiceClientError,
    type WorkflowWebClient,
} from '@workgraph/workflow-service-web-client';
import { environment } from '../../environments/environment';
import { AuthService } from '../services/auth.service';
import type {
    BpmnResponse,
    CreateWorkflowRequest,
    FlowIssue,
    FlowResponse,
    FlowView,
    PublishResponse,
    UpdateWorkflowRequest,
    Workflow,
} from '../models/workflow.model';

/** How the caught error was classified — components branch on this, never on message text. */
export type WorkflowErrorKind =
    | 'not-found'
    | 'conflict'
    | 'name-taken'
    | 'publish-refused'
    | 'invalid'
    | 'downstream-unavailable'
    | 'generic';

/**
 * What a 409 tells us about the newer draft: its version and who changed it, when.
 * `ConflictDetails.none()` is the null object carried by every non-conflict error (and by a
 * 409 whose body is absent or malformed).
 */
export class ConflictDetails {
    constructor(
        readonly currentVersion: number,
        readonly editedBy: string,
        readonly editedAt: string,
    ) {}

    static none(): ConflictDetails {
        return new ConflictDetails(0, '', '');
    }

    isNone(): boolean {
        return this.currentVersion === 0 && this.editedBy === '';
    }
}

/**
 * A renderable, typed error the components can branch on by KIND rather than message text.
 *
 * `.message` is always the user-facing copy; `.kind` and `.status` derive from the client's
 * `statusCode`; `.conflict` (409) and `.issues` (422) derive from the parsed error body.
 */
export class WorkflowError extends Error {
    constructor(
        message: string,
        readonly kind: WorkflowErrorKind,
        readonly status?: number,
        readonly conflict: ConflictDetails = ConflictDetails.none(),
        readonly issues: FlowIssue[] = [],
    ) {
        super(message);
        this.name = 'WorkflowError';
    }
}

const DOWNSTREAM_UNAVAILABLE = 'The agent service is unavailable, try again.';

/**
 * Thin Angular seam between DI and the generated workflow-service web-client.
 *
 * Mirrors RoleGateway: (1) constructs the web-client with the api-gateway host and the Cognito
 * bearer token, (2) bridges promises to Observables, (3) unwraps the `{data}` / `{data,count}`
 * envelopes, and (4) maps errors to typed `WorkflowError`s. Classification reads ONLY
 * `ServiceClientError.statusCode` and `.body` — never message text (`statusText` is empty over
 * HTTP/2).
 */
@Injectable({ providedIn: 'root' })
export class WorkflowGateway {
    private readonly auth = inject(AuthService);

    private readonly client: WorkflowWebClient = createWorkflowWebClient({
        baseUrl: environment.gatewayBaseUrl,
        getToken: () => this.auth.getIdToken(),
    });

    listWorkflows(projectId: string): Observable<Workflow[]> {
        return from(this.client.listWorkflows({ path: { projectId } })).pipe(
            map((result) => result.data?.data ?? []),
            catchError((err) =>
                this.fail(err, 'We could not load the workflows. Please try again.'),
            ),
        );
    }

    getWorkflow(projectId: string, workflowId: string): Observable<Workflow> {
        return from(this.client.getWorkflow({ path: { projectId, workflowId } })).pipe(
            map((result) => this.requireData(result.data?.data, 'workflow')),
            catchError((err) =>
                this.fail(err, 'We could not load this workflow. Please try again.'),
            ),
        );
    }

    createWorkflow(projectId: string, body: CreateWorkflowRequest): Observable<Workflow> {
        return from(this.client.createWorkflow({ path: { projectId }, body })).pipe(
            map((result) => this.requireData(result.data?.data, 'workflow')),
            catchError((err) =>
                this.fail(err, 'We could not create the workflow. Please try again.'),
            ),
        );
    }

    updateWorkflow(
        projectId: string,
        workflowId: string,
        body: UpdateWorkflowRequest,
    ): Observable<Workflow> {
        return from(this.client.updateWorkflow({ path: { projectId, workflowId }, body })).pipe(
            map((result) => this.requireData(result.data?.data, 'workflow')),
            catchError((err) =>
                this.fail(err, 'We could not update the workflow. Please try again.'),
            ),
        );
    }

    deleteWorkflow(projectId: string, workflowId: string): Observable<void> {
        return from(this.client.deleteWorkflow({ path: { projectId, workflowId } })).pipe(
            map(() => void 0),
            catchError((err) =>
                this.fail(err, 'We could not delete the workflow. Please try again.'),
            ),
        );
    }

    archiveWorkflow(projectId: string, workflowId: string): Observable<Workflow> {
        return from(this.client.archiveWorkflow({ path: { projectId, workflowId } })).pipe(
            map((result) => this.requireData(result.data?.data, 'workflow')),
            catchError((err) =>
                this.fail(err, 'We could not archive the workflow. Please try again.'),
            ),
        );
    }

    restoreWorkflow(projectId: string, workflowId: string): Observable<Workflow> {
        return from(this.client.restoreWorkflow({ path: { projectId, workflowId } })).pipe(
            map((result) => this.requireData(result.data?.data, 'workflow')),
            catchError((err) =>
                this.fail(err, 'We could not restore the workflow. Please try again.'),
            ),
        );
    }

    getFlow(projectId: string, workflowId: string): Observable<FlowResponse> {
        return from(this.client.getFlow({ path: { projectId, workflowId } })).pipe(
            map((result) => this.requireData(result.data?.data, 'flow')),
            catchError((err) => this.fail(err, 'We could not load the flow. Please try again.')),
        );
    }

    putFlow(
        projectId: string,
        workflowId: string,
        version: number,
        flow: FlowView,
    ): Observable<FlowResponse> {
        return from(
            this.client.putFlow({ path: { projectId, workflowId }, body: { version, flow } }),
        ).pipe(
            map((result) => this.requireData(result.data?.data, 'flow')),
            catchError((err) => this.fail(err, 'We could not save the draft. Please try again.')),
        );
    }

    exportBpmn(projectId: string, workflowId: string): Observable<BpmnResponse> {
        return from(this.client.exportBpmn({ path: { projectId, workflowId } })).pipe(
            map((result) => this.requireData(result.data?.data, 'BPMN')),
            catchError((err) =>
                this.fail(err, 'We could not export the workflow. Please try again.'),
            ),
        );
    }

    importBpmn(
        projectId: string,
        workflowId: string,
        version: number,
        bpmn: string,
    ): Observable<FlowResponse> {
        return from(
            this.client.importBpmn({ path: { projectId, workflowId }, body: { version, bpmn } }),
        ).pipe(
            map((result) => this.requireData(result.data?.data, 'flow')),
            catchError((err) =>
                this.fail(err, 'We could not import the BPMN file. Please try again.'),
            ),
        );
    }

    publish(projectId: string, workflowId: string, version: number): Observable<PublishResponse> {
        return from(
            this.client.publishWorkflow({ path: { projectId, workflowId }, body: { version } }),
        ).pipe(
            map((result) => this.requireData(result.data?.data, 'release')),
            catchError((err) =>
                this.fail(err, 'We could not publish the workflow. Please try again.'),
            ),
        );
    }

    discardDraft(projectId: string, workflowId: string, version: number): Observable<FlowResponse> {
        return from(
            this.client.discardDraft({ path: { projectId, workflowId }, body: { version } }),
        ).pipe(
            map((result) => this.requireData(result.data?.data, 'flow')),
            catchError((err) =>
                this.fail(err, 'We could not discard the draft. Please try again.'),
            ),
        );
    }

    /** Guard against a 2xx with an unexpectedly empty envelope. */
    private requireData<T>(data: T | undefined | null, what: string): T {
        if (data === undefined || data === null) {
            throw new Error(`The server returned no ${what} data.`);
        }
        return data;
    }

    private fail(err: unknown, fallback: string): Observable<never> {
        return throwError(() => this.toWorkflowError(err, fallback));
    }

    /** Classify by `statusCode` and the parsed `body` only — never by message text. */
    private toWorkflowError(err: unknown, fallback: string): WorkflowError {
        if (err instanceof ServiceClientError) {
            const status = err.statusCode;
            const body = asRecord(err.body);
            const specific =
                this.meaningfulMessage(typeof body['error'] === 'string' ? body['error'] : '') ??
                this.meaningfulMessage(err.message);

            switch (status) {
                case 404:
                    return new WorkflowError(
                        specific ?? 'This workflow was not found.',
                        'not-found',
                        404,
                    );
                case 409:
                    // A version conflict always carries `currentVersion`; a name conflict is a
                    // plain `{ error }`. A 409 with no body stays a version conflict.
                    if (typeof body['error'] === 'string' && !('currentVersion' in body)) {
                        return new WorkflowError(
                            specific ?? 'Another workflow in this project already has this name.',
                            'name-taken',
                            409,
                        );
                    }
                    return new WorkflowError(
                        'This draft was changed elsewhere. Reload to get the latest version.',
                        'conflict',
                        409,
                        conflictFrom(body),
                    );
                case 422:
                    return new WorkflowError(
                        'The workflow has errors and was not published.',
                        'publish-refused',
                        422,
                        ConflictDetails.none(),
                        issuesFrom(body),
                    );
                case 400:
                    return new WorkflowError(specific ?? fallback, 'invalid', 400);
                case 502:
                    return new WorkflowError(DOWNSTREAM_UNAVAILABLE, 'downstream-unavailable', 502);
                default:
                    return new WorkflowError(specific ?? fallback, 'generic', status);
            }
        }
        if (err instanceof Error && err.message) {
            return new WorkflowError(err.message, 'generic');
        }
        return new WorkflowError(fallback, 'generic');
    }

    /**
     * The message only when it carries something meaningful — non-empty AND not a bare HTTP
     * reason phrase. `undefined` otherwise so callers fall back.
     */
    private meaningfulMessage(raw: string | undefined | null): string | undefined {
        const message = (raw ?? '').trim();
        if (!message || this.isGenericHttpMessage(message)) {
            return undefined;
        }
        return message;
    }

    private isGenericHttpMessage(message: string): boolean {
        const generic = [
            'bad request',
            'not found',
            'conflict',
            'unprocessable entity',
            'bad gateway',
            'internal server error',
            'forbidden',
            'unauthorized',
        ];
        const normalized = message.toLowerCase();
        return (
            generic.some((g) => normalized === g) ||
            normalized.startsWith('request failed with status')
        );
    }
}

/** The body as a record (empty when absent, not JSON, or not an object). */
function asRecord(body: unknown): Record<string, unknown> {
    return typeof body === 'object' && body !== null && !Array.isArray(body)
        ? (body as Record<string, unknown>)
        : {};
}

/** 409 details from the body, each field defaulted when absent or of the wrong type. */
function conflictFrom(body: Record<string, unknown>): ConflictDetails {
    const currentVersion = body['currentVersion'];
    const editedBy = body['editedBy'];
    const editedAt = body['editedAt'];
    return new ConflictDetails(
        typeof currentVersion === 'number' && Number.isFinite(currentVersion) ? currentVersion : 0,
        typeof editedBy === 'string' ? editedBy : '',
        typeof editedAt === 'string' ? editedAt : '',
    );
}

/** 422 issues from the body, keeping only well-formed entries. */
function issuesFrom(body: Record<string, unknown>): FlowIssue[] {
    const issues = body['issues'];
    if (!Array.isArray(issues)) {
        return [];
    }
    return issues.filter(isFlowIssue);
}

function isFlowIssue(value: unknown): value is FlowIssue {
    const issue = asRecord(value);
    return (
        typeof issue['code'] === 'string' &&
        (issue['severity'] === 'error' || issue['severity'] === 'warning') &&
        typeof issue['subjectId'] === 'string' &&
        typeof issue['message'] === 'string'
    );
}
